import {MegaScreen} from './MegaScreen.js';
import {formatElapsed, notNil, logObject} from '@megaphonenpm/mputils/src/utils.js';
import {warn} from '@megaphonenpm/mputils/src/logfunctions.js';
import {get} from 'lodash-es';
import {MRTSConnection} from '@megaphonenpm/mrtsclient/src/MRTSConnection.js';
import {isGWDThemeValid} from './GWDService.js';
import {CHANGE, CLOSE, READY, START} from '@megaphonenpm/mputils/src/constants.js';
import {DATA_RUN, SCREEN_READY} from '@megaphonenpm/mrtsclient/src/MRTSMessages.js';
import {STATE_SCENE, STATE_SCENE_RESULT} from '@megaphonenpm/mrtsclient/src/constants.js';
// Required because moment-precise-range does not provide an ES6 module implementation
import {addPreciseRangeToMoment} from '@megaphonenpm/mputils/src/addPreciseRangeToMoment.js';
import {DebugModel} from '@megaphonenpm/mpui/src/DebugModel.js';
import moment from 'moment/src/moment.js';

/**
 * Manages the collection and dislay of debugging information for the in-app debugger.
 */
export class DebugManager {
  constructor (bootTime, bootManager) {
    // Create DebugModel instance
    this.debugModel = new DebugModel();

    // Configure boot information
    this.bootTime = bootTime;
    this.debugModel.setBootManager(bootManager);

    // Extend moment
    addPreciseRangeToMoment(moment);

    // Configure MRTS connection information
    this.mrtsConnectionAttributeWhitelist = new Map();
    this.mrtsConnectionAttributeWhitelist.set('_IP','IP Address');
    this.mrtsConnectionAttributeWhitelist.set('_CONNECTION_TYPE','MRTS Connection Type');
    this.mrtsConnectionAttributeWhitelist.set('_USERAGENT','User Agent');
    this.mrtsConnectionAttributeWhitelist.set('INSTANCE_ID','MRTS Instance ID');

    MegaScreen.globals.mousetrap.bind('d s', () => {
      MegaScreen.globals.dataStore.logClone();
    });

    MegaScreen.globals.mousetrap.bind('a s', () => {
      logObject(MegaScreen.globals.appState, 'AppState');
    });

    // Begin monitoring for changes in the app state
    this.monitor();
  }

//==================================================================================================
// DEPENDENCIES
//==================================================================================================

  /**
   * Supplies access to the application's general data model (appState).
   *
   * @param appState An AppState instance.
   */
  setAppState (appState) {
    this.appState = appState;
    this.registerAppStateListener(this.appState);
    this.refreshStateStat();
  }

  setDebugPanel (debugPanel) {
    debugPanel.setDebugModel(this.debugModel);
    // TODO: Replace these assignments with a more general DebugPanel refresh() method to synchronize
    //   the panel with the DebugModel's stats.
    this.debugModel.setBootTime(this.bootTime);
    this.refreshReleaseInfo();
  }

  setMRTSConnection (mrtsConnection) {
    this.mrtsConnection = mrtsConnection;
    this.registerMRTSConnectionListeners();
  }

  setReleaseInfo (releaseInfo) {
    this.releaseInfo = releaseInfo;
    this.refreshReleaseInfo();
  }

//==================================================================================================
// METHODS
//==================================================================================================

  monitor () {
    this.interval = setInterval(() => {
      let now = moment();
      let elapsedSinceBoot = moment.preciseDiff(this.bootTime.value, moment(), true);
      let elapsedSinceReady = null;

      // Elapsed Since Boot
      this.debugModel.setStat('elapsedsinceboot', 'Elapsed Since Boot', formatElapsed(elapsedSinceBoot));

      // Elaspsed Since Screen Ready
      if (notNil(this.mrtsConnection)) {
        if (this.mrtsConnection.isScreenReady) {
          elapsedSinceReady = moment.preciseDiff(this.mrtsConnection.screenReadyTime, moment(), true);
          this.debugModel.setStat('elapsedsinceready', 'Elapsed Since Screen Ready', formatElapsed(elapsedSinceReady));
        } else {
          this.debugModel.setStat('elapsedsinceready', 'Elapsed Since Screen Ready', null);
        }

        // Orbiter stats
        if (notNil(this.mrtsConnection.orbiter.self())) {
          let ping = this.mrtsConnection.orbiter.self().getPing();
          let pingHealth = null;
          if (ping > 0) {
            pingHealth = ping < 500 ? 100 : (ping < 2500 ? 50 : 0);
          }
          this.debugModel.setStat('ping', 'Ping', ping, pingHealth);
        }

        let connectionManager = this.mrtsConnection.orbiter.getConnectionManager();

        this.debugModel.setStat('hostrequetsed', 'Host Requested', get(MegaScreen.globals.dataStore, 'mrtsEndpoint.host'));
        this.debugModel.setStat('hostactual', 'Host Actual', connectionManager.getAffinity(get(MegaScreen.globals.dataStore, 'mrtsEndpoint.host')));
        this.debugModel.setStat('connectionsaborted', 'Connections Aborted', connectionManager.getConnectAbortCount());
        this.debugModel.setStat('connectionsattempted', 'Connections Attempted', connectionManager.getConnectAttemptCount());
        this.debugModel.setStat('connectionsfailed', 'Connections Failed', connectionManager.getConnectFailedCount());
        this.debugModel.setStat('connectionstate', 'Connection State', MRTSConnection.HUMAN_READABLE_CONNECTION_STATES.get(connectionManager.getConnectionState()));
        this.debugModel.setStat('orbiterconnectionreadies', 'Connection Readies', connectionManager.getReadyCount());
      }

      // Themes
      let loadedThemeIDs = Array.from(MegaScreen.globals.themeLoader.loadedThemes);
      let loadedThemeNames = [];
      let failedThemeNames = [];

      loadedThemeIDs.forEach(themeID => {
        let themeName = get(MegaScreen.globals.themeService.getTheme(themeID), 'name', themeID);
        if (isGWDThemeValid(themeID)) {
          loadedThemeNames.push(themeName);
        } else {
          failedThemeNames.push(themeName);
        }
      });

      this.debugModel.setStat('themesloaded', 'Themes Loaded', loadedThemeNames.join(', '));
      this.debugModel.setStat('themesfailed', 'Themes Failed', failedThemeNames.join(', '), failedThemeNames.length > 0 ? 0 : 100);

    }, 5000);
  }

  registerMRTSConnectionListeners () {
    this.mrtsConnection.on(READY, e => {
      this.debugModel.setStat('timetoorbiterready', 'MRTS Time to Orbiter Ready', this.mrtsConnection.timeToOrbiterReady);
      this.debugModel.setStat('unionclientid', 'MRTS Union Client ID', this.mrtsConnection.orbiter.getClientID());
    });
    this.mrtsConnection.on(SCREEN_READY, e => {
      this.debugModel.setStat('timetoscreenready', 'MRTS Time to Screen Ready', this.mrtsConnection.timeToScreenReady);
      this.debugModel.setStat('screenready', 'Screen Ready', 'Yes', 100);
      this.debugModel.setStat('screenreadies', 'Screen Readies', this.mrtsConnection.numScreenReadies);
    });
    this.mrtsConnection.on(DATA_RUN, e => {
      this.debugModel.setStat('roomstart', 'MRTS Room Start', moment(e.detail.roomStart));
      this.debugModel.setStat('runid', 'MRTS Run ID', e.detail.runID);
      this.debugModel.setStat('runstart', 'MRTS Run Start', moment(e.detail.runStart));
    });
    this.mrtsConnection.on(CLOSE, e => {
      this.debugModel.setStat('screenready', 'Screen Ready', 'No', 0);
    });
    this.mrtsConnection.on(CHANGE, e => {
      if (this.mrtsConnectionAttributeWhitelist.has(e.detail.name)) {
        this.debugModel.setStat(e.detail.name, this.mrtsConnectionAttributeWhitelist.get(e.detail.name), e.detail.value);
      }
    });
  }

  registerAppStateListener () {
    this.appState.on(CHANGE, e => {
      switch (e.detail.prop) {
        case 'timeRemaining':
          this.refreshTimeRemaining()
          break;

        case 'numUsers':
          this.refreshNumUsers();
          break;

        case 'votes':
          this.refreshVotes();
          break;

        case 'state':
          warn('App State changed to: ' + e.detail.value);
          this.refreshAppState();

          break;
      }
    });
  }

  refreshStateStat () {
    this.debugModel.setStat('appstate', 'State', this.appState.state);
  }

  refreshTimeRemaining () {
    this.debugModel.setStat('timeRemaining', 'Time Remaining', this.appState.timeRemaining);
  }

  refreshNumUsers () {
    this.debugModel.setStat('numUsers', 'Users Online', this.appState.numUsers);

  }

  refreshVotes () {
    this.debugModel.setStat('votes', 'Votes', this.appState.votes);
    // reduce() totals the integers in the array
    this.debugModel.setStat('usersAnswered', 'Users Answered', this.appState.votes.reduce((a, b) => a + b, 0));
  }

  refreshAppState () {
    this.refreshTimeRemaining();
    this.refreshNumUsers();
    this.refreshVotes();

    this.debugModel.setStat('playlistid', 'Playlist ID', get(MegaScreen.globals.dataStore, 'playlist.id'));
    this.debugModel.setStat('playlistname', 'Playlist Name', get(MegaScreen.globals.dataStore, 'playlist.name'));
    this.refreshStateStat();
    let theme = MegaScreen.globals.themeService.getCurrentScreenTheme();
    this.debugModel.setStat('themename', 'Theme Name', get(theme, 'name'));
    this.debugModel.setStat('themeid', 'Theme ID', get(theme, 'id'));

    if (this.appState.state === STATE_SCENE || this.appState.state === STATE_SCENE_RESULT) {
      this.debugModel.setStat('sceneid', 'Scene ID', get(this.appState.scene, 'id'));
      this.debugModel.setStat('sceneindex', 'Scene Index', MegaScreen.globals.playlistService.getSceneIndex(this.appState.scene));
      this.debugModel.setStat('productid', 'Product ID', get(this.appState.scene, 'presentation.screen_product.id'));
      this.debugModel.setStat('productname', 'Product Name', get(this.appState.scene, 'presentation.screen_product.name'));
    }
  }

  refreshReleaseInfo () {
    this.debugModel.setStat('appversion', `${this.releaseInfo.title} Version`, this.releaseInfo.version);
  }
}
